import React, {useCallback, useRef} from 'react';
import {StyleSheet, View} from 'react-native';

import {toBottleCount} from '../../../utils/coffeeCard';

import AnimatedBottle, {FILL_DURATION_MS} from './AnimatedBottle';
import {BottleVariant} from './bottleGeometry';

/**
 * A coffee card's row of bottles, with the entry animation.
 *
 * Every earned bottle fills at the same time. There is no stagger and no
 * per-bottle offset, so the row always finishes in `fillDurationMs` no matter
 * how many bottles it holds — one bottle and five bottles take exactly as long.
 *
 * The offer payload is the only input. The row keeps no "already played" state
 * and does not work out whether the member has seen the fill before, so:
 *
 *   - a new mount pours;
 *   - a changed `filledCount` pours, because the bottle keys carry it and the
 *     row therefore remounts;
 *   - a repeated identical payload does nothing, because nothing remounts.
 */

export type BottleRowProps = {
  /** Which bottle to draw. Every bottle in a row is the same style. */
  variant?: BottleVariant;
  /** Bottles the member has earned this cycle. */
  filledCount: number;
  /** Bottles still to earn. */
  emptyCount: number;
  height: number;
  color: string;
  fillDurationMs?: number;
  idleMotion?: boolean;
  /** Called once every earned bottle in the row has settled. */
  onRowComplete?: () => void;
  testID?: string;
};

const BottleRow = ({
  variant = 'wine',
  filledCount: rawFilledCount,
  emptyCount: rawEmptyCount,
  height,
  color,
  fillDurationMs = FILL_DURATION_MS,
  idleMotion = true,
  onRowComplete,
  testID = 'coffee-card-bottle-row',
}: BottleRowProps) => {
  // DIS-1913: `Array(n)` throws for a negative, fractional or infinite n.
  const filledCount = toBottleCount(rawFilledCount);
  const emptyCount = toBottleCount(rawEmptyCount);
  const completedRef = useRef(0);
  const pouredCountRef = useRef(filledCount);
  const onRowCompleteRef = useRef(onRowComplete);
  onRowCompleteRef.current = onRowComplete;

  // A changed count is a fresh pour, so the tally starts again. Reset during
  // render, not in an effect: the bottles remount on the same commit (their
  // keys carry `filledCount`) and a stale tally would leave the row one short
  // of `filledCount` forever, so `onRowComplete` would never fire again.
  if (pouredCountRef.current !== filledCount) {
    pouredCountRef.current = filledCount;
    completedRef.current = 0;
  }

  const handleBottleComplete = useCallback(() => {
    completedRef.current += 1;
    if (completedRef.current < filledCount) {
      return;
    }

    onRowCompleteRef.current?.();
  }, [filledCount]);

  return (
    <View testID={testID} style={styles.row}>
      {[...Array(filledCount)].map((_, index) => (
        <AnimatedBottle
          key={`filled-${filledCount}-${index}`}
          testID={`coffee-card-bottle-filled-${index}`}
          variant={variant}
          height={height}
          color={color}
          filled
          animateEntry
          fillDurationMs={fillDurationMs}
          idleMotion={idleMotion}
          onFillComplete={handleBottleComplete}
        />
      ))}

      {[...Array(emptyCount)].map((_, index) => (
        <AnimatedBottle
          key={`empty-${index}`}
          testID={`coffee-card-bottle-empty-${index}`}
          variant={variant}
          height={height}
          color={color}
          filled={false}
          idleMotion={false}
        />
      ))}
    </View>
  );
};

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'center',
    flexWrap: 'nowrap',
    gap: 8,
  },
});

export default BottleRow;
